import { cva, type VariantProps } from 'class-variance-authority'
import {
  AlertCircleIcon,
  CheckCircleIcon,
  CircleDotIcon,
  Loader2Icon,
  MinusCircleIcon,
} from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { ToolCallStatus } from './chat-message.interface'

const chatToolCallBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
  {
    variants: {
      status: {
        pending: 'bg-muted text-muted-foreground',
        running: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
        completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
        'no-data': 'bg-muted text-muted-foreground',
        error: 'bg-destructive/10 text-destructive',
      },
    },
    defaultVariants: {
      status: 'pending',
    },
  }
)

export interface ChatToolCallBadgeProps extends VariantProps<typeof chatToolCallBadgeVariants> {
  status: ToolCallStatus

  label?: string

  className?: string
}

const StatusIcon: React.FC<{ status: ToolCallStatus }> = ({ status }) => {
  const iconProps = { className: 'size-3.5' }

  const iconMap: Record<ToolCallStatus, ReactNode> = {
    pending: <CircleDotIcon {...iconProps} />,
    running: <Loader2Icon {...iconProps} className={cn(iconProps.className, 'animate-spin')} />,
    completed: <CheckCircleIcon {...iconProps} />,
    'no-data': <MinusCircleIcon {...iconProps} />,
    error: <AlertCircleIcon {...iconProps} />,
  }

  return iconMap[status]
}

const defaultLabels: Record<ToolCallStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  'no-data': 'No data retrieved',
  error: 'Error',
}

const ChatToolCallBadgeBase: React.FC<ChatToolCallBadgeProps> = ({ status, label, className }) => (
  <span className={cn(chatToolCallBadgeVariants({ status }), className)}>
    <StatusIcon status={status} />
    {label ?? defaultLabels[status]}
  </span>
)

export const ChatToolCallBadge = memo(ChatToolCallBadgeBase)

ChatToolCallBadge.displayName = 'ChatToolCallBadge'

export { chatToolCallBadgeVariants }
